import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BondStatus } from '../api';
import {
  MovementPayload, StatusReason, alterarSituacao, aprovarMovimentacao, cancelarMovimentacao, listarAreasAtivas,
  listarCargosAtivos, listarCentrosDeCustoAtivos, listarLocalidadesAtivas, listarMotivos, lotacoesDoVinculo,
  movimentacoesDoVinculo, movimentacoesPendentes, registrarMovimentacao, salvarMotivo,
} from '../api/vidaFuncional';
import { PESSOAS_KEYS } from './index';

export const VF_KEYS = {
  lotacoes: (bondId: string) => ['pessoas', 'lotacoes', bondId] as const,
  movimentacoes: (bondId: string) => ['pessoas', 'movimentacoes', bondId] as const,
  pendentes: ['pessoas', 'movimentacoes', 'pendentes'] as const,
  motivos: (status: string, all: boolean) => ['pessoas', 'motivos', status, all] as const,
  estruturas: (kind: string) => ['pessoas', 'estruturas', kind] as const,
};

function useWrite<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: PESSOAS_KEYS.all }) });
}

export const useLotacoes = (bondId: string | undefined, enabled = true) =>
  useQuery({ queryKey: VF_KEYS.lotacoes(bondId ?? ''), queryFn: () => lotacoesDoVinculo(bondId as string), enabled: !!bondId && enabled });

export const useMovimentacoes = (bondId: string | undefined, enabled = true) =>
  useQuery({ queryKey: VF_KEYS.movimentacoes(bondId ?? ''), queryFn: () => movimentacoesDoVinculo(bondId as string), enabled: !!bondId && enabled });

export const usePendentesAprovacao = () => useQuery({ queryKey: VF_KEYS.pendentes, queryFn: movimentacoesPendentes });

export const useRegistrarMovimentacao = (bondId: string) =>
  useWrite((payload: MovementPayload) => registrarMovimentacao(bondId, payload));
export const useAprovarMovimentacao = () => useWrite((id: string) => aprovarMovimentacao(id));
export const useCancelarMovimentacao = () =>
  useWrite(({ id, justification }: { id: string; justification: string }) => cancelarMovimentacao(id, justification));

export const useAlterarSituacao = () =>
  useWrite(({ bondId, ...payload }: { bondId: string; status: BondStatus; justification: string; reasonId?: string; date?: string }) =>
    alterarSituacao(bondId, payload));

export const useMotivos = (status = '', all = false, enabled = true) =>
  useQuery({ queryKey: VF_KEYS.motivos(status, all), queryFn: () => listarMotivos(status || undefined, all), enabled });
export const useSalvarMotivo = () =>
  useWrite(({ id, payload }: { id: string | null; payload: Partial<StatusReason> }) => salvarMotivo(id, payload));

const STRUCTURES = {
  areas: listarAreasAtivas,
  cargos: listarCargosAtivos,
  centros: listarCentrosDeCustoAtivos,
  localidades: listarLocalidadesAtivas,
};

export const useEstruturas = (kind: keyof typeof STRUCTURES, enabled = true) =>
  useQuery({ queryKey: VF_KEYS.estruturas(kind), queryFn: STRUCTURES[kind], enabled, staleTime: 5 * 60_000 });
